import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UpdateStopperRuleMutationArguments,
  type UpdateStopperRuleResponse,
} from './types';
import { zodUpdateStopperRuleSchema } from './validations';

/**
 * Updates a stopper rule for an invoice process.
 * @param stopperRuleId - The ID of the invoice stopper rule to update.
 * @returns A promise that resolves when the stopper rule is updated.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_process_stopper_rule_vadmin_invoice_stopper_rule__stopper_rule_id__put
 */
export const updateInvoiceProcessStopperRule = async ({
  stopperRuleId,
  updateStopperRulePayload,
}: UpdateStopperRuleMutationArguments) => {
  try {
    const response = await vestigasApi
      .put(ENDPOINT.INVOICE_REPROCESSING.UPDATE_STOPPER_RULE(stopperRuleId), {
        json: updateStopperRulePayload,
        validate: { body: zodUpdateStopperRuleSchema },
      })
      .json<UpdateStopperRuleResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating invoice process stopper rule',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.updateStopperRule.error404, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.updateStopperRule.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.updateStopperRule.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
